import { inject, Injectable, signal } from '@angular/core';
import { ConverterService, EndToEndEncrypter, Guid, StammdatenService } from '@ebeihilfe/core';
import { Model, PanelModel, QuestionMultipleTextModel, SurveyModel } from 'survey-core';

import { AntragMetaDataDtoAssemblerService } from '../dto-assembler/antrag-meta-data-dto-assembler.service';
import { AntragMetaData, FormDocument, FormResult, FormValue } from '../model';

@Injectable({
  providedIn: 'root',
})
export class MetadataHelpersService {
  private readonly end2EndEncrypter = inject(EndToEndEncrypter);
  private readonly antragMetaDataDtoAssemblerService = inject(AntragMetaDataDtoAssemblerService);
  private readonly converterService = inject(ConverterService);
  private readonly stammdatenService = inject(StammdatenService);
  public surveyModel = signal(new Model());

  /**
   * Creates a temporary object from the given Survey.js form and state to update metadata in the store.
   * todo Copied and combined from multiple components, should be done in reducer.
   *
   * @param {SurveyModel} surveyModel - The Survey.js form model containing data and its validation state.
   * @param {AntragMetaData} metadata - The existing metadata to update with the new form data.
   * @param {FormDocument} documentForm - The form document containing additional metadata such as form identifier and mandatoriness.
   * @return {AntragMetaData} The updated or newly created temporary metadata object with the form data appended.
   */
  public createTemporaryObjectFromSurveyJsAndStateToUpdateMetadataInStore(
    surveyModel: SurveyModel,
    metadata: AntragMetaData | undefined,
    documentForm: FormDocument,
  ): AntragMetaData {
    const formValueResults: FormValueResultMap[] = [];
    surveyModel.getAllPanels().forEach((panel) => {
      const panelModel = panel as PanelModel;
      panelModel.questions.forEach((question) => {
        if (!question.isVisibleInSurvey || !question.isVisible || question.value == null || question.value === '') {
          return;
        }
        this.buildFormValues(question).forEach((value) =>
          formValueResults.push({ key: panelModel.getPropertyValue('WordTemplateName'), value }),
        );
      });
    });

    const uniqueFormValueResults = [
      ...new Map(
        formValueResults.map((item) => [`${item.value.$type}-${item.value.name}-${item.value.parent}`, item]),
      ).values(),
    ];

    const formResults = this.createFormResults(
      uniqueFormValueResults,
      documentForm.formId,
      surveyModel.validate(false),
      documentForm.isMandatory,
    );

    if (!formResults && uniqueFormValueResults.length === 0) {
      throw new Error('No panel found in survey');
    }
    if (metadata != null) {
      return this.addOrUpdateMetadata(metadata, formResults);
    } else {
      return new AntragMetaData({ formResults: formResults });
    }
  }

  public async deserialize(encryptedMetadata: string): Promise<AntragMetaData> {
    const arrayBuffer = await this.end2EndEncrypter.decryptFromBase64(JSON.stringify(encryptedMetadata));

    if (arrayBuffer == null) {
      throw new Error('Could not decrypt metadata');
    }

    const decryptedMetadataJson = this.converterService.uIntArrayToString(arrayBuffer);

    return this.antragMetaDataDtoAssemblerService.fromDto(JSON.parse(decryptedMetadataJson));
  }

  public async serialize(metaData: AntragMetaData): Promise<string> {
    const antragMetaDataDto = this.antragMetaDataDtoAssemblerService.toDto(metaData);

    const serializedMetadata = await this.end2EndEncrypter.encryptToBase64(JSON.stringify(antragMetaDataDto));
    return serializedMetadata ?? '';
  }

  public async serializeSurveyData(data: Record<string, unknown>): Promise<string> {
    const encrypted = await this.end2EndEncrypter.encryptToBase64(JSON.stringify(data));
    return encrypted ?? '';
  }

  public clearSurveyValueIfPanelIsInvisible(
    survey: Model,
    mode: 'onHidden' | 'onHiddenContainer' = 'onHiddenContainer',
  ): void {
    survey.getAllQuestions().forEach((question) => {
      question.clearIfInvisible = mode;
    });
  }

  /**
   * Reconstructs a flat Survey.js data object from persisted form results.
   *
   * Translates the domain representation of stored answers ({@link FormResult}[]) into the
   * key-value format that Survey.js expects as its `data` input, allowing a previously saved
   * antrag to be restored and pre-filled in the survey form.
   *
   * Each {@link FormValue} is processed according to its type:
   * - **radiogroup** (stored as individual checkboxes with a `parent` key): single values are
   *   merged under their shared parent key and accumulated into an array if multiple selections exist.
   * - **all other types**: written directly as `{ [name]: value }`.
   *
   * @param results - The persisted form results from the antrag metadata store.
   * @returns A flat record mapping Survey.js question names to their restored answer values,
   *          ready to be passed as the survey's `data` property.
   */
  public buildSurveyDataFromResults(results: FormResult[]): Record<string, FormValue['value']> {
    const targetData: Record<string, FormValue['value']> = {};
    results.forEach((formResult) => {
      formResult.results.forEach((value) => this.processFormValue(targetData, value));
    });
    return targetData;
  }

  private processFormValue(targetData: Record<string, FormValue['value']>, value: FormValue): void {
    // Case 1: Group Element: radiogroup (Einzelne Checkbox, die gesammelt werden muss wegen z.B gleicher parent key)
    if (value.$type === 'checkbox' && value.parent && value.value === true && !Array.isArray(value.name)) {
      this.mergeSingleGroupValue(targetData, value.parent, value.name);
      return;
    }

    // Case 2: Standard (Einfaches Überschreiben)
    targetData[value.name] = value.value;
  }

  private mergeSingleGroupValue(
    targetData: Record<string, FormValue['value']>,
    parentKey: string,
    newValue: string,
  ): void {
    const existingValue = targetData[parentKey];

    // Fall 1: Noch kein Eintrag -> Wert einfach setzen
    if (existingValue === undefined) {
      targetData[parentKey] = newValue;
      return;
    }

    // Fall 2: Bereits eine Liste -> Wert hinzufügen
    if (Array.isArray(existingValue)) {
      if (!existingValue.includes(newValue)) {
        existingValue.push(newValue);
      }
      return;
    }

    // Fall 3: Ein Einzelwert existiert -> Umwandeln in Array
    if (existingValue !== newValue) {
      targetData[parentKey] = [existingValue, newValue];
    }
  }

  /** Maps a question to its FormValues. A `multipletext` recurses per item, treating each as its own input. */
  private buildFormValues(question: MappableQuestion, parent?: string): FormValue[] {
    if (question.getType() === 'checkbox') {
      return (question.value as string[]).map((name) => FormValue.create('checkbox', name, true, question.name));
    }

    // map radiogroup to checkbox
    if (question.getType() === 'radiogroup') {
      return [FormValue.create('checkbox', question.value as string, true, question.name)];
    }

    // Time-only field (WI 143285): store under a dedicated 'time' type as a 24h `HH:mm:ss` string,
    // so the backend and Word replacement can distinguish it from datetime values.
    if (this.isTimeQuestion(question)) {
      return [FormValue.create('time', question.name, this.formatTimeValue(question.value), parent)];
    }

    // Multiple Textboxes (SurveyJS `multipletext`): recurse with each item as its own text input so it
    // runs through the same type handling (e.g. time), remembering the multipletext question as parent.
    if (question.getType() === 'multipletext') {
      return (question as QuestionMultipleTextModel).items.flatMap((item) => {
        const itemValue = (question.value as Record<string, unknown> | null)?.[item.name];
        if (itemValue == null || itemValue === '') {
          return [];
        }
        const itemQuestion: MappableQuestion = {
          getType: () => 'text',
          name: item.name,
          value: itemValue,
          inputType: item.inputType,
        };
        return this.buildFormValues(itemQuestion, question.name);
      });
    }

    // force value to string if question type is text, because in some cases it can be number and we want to avoid that
    if (question.getType() === 'text' && typeof question.value !== 'string' && !!question.value) {
      return [FormValue.create(question.getType(), question.name, String(question.value), parent)];
    }

    return [FormValue.create(question.getType(), question.name, question.value, parent)];
  }

  /**
   * A SurveyJS time-only field is a text question with `inputType: 'time'` (the approach from the
   * SurveyJS datetime-entry docs); an explicit `time` question type is also accepted defensively.
   */
  private isTimeQuestion(question: MappableQuestion): boolean {
    return question.getType() === 'time' || (question.getType() === 'text' && question.inputType === 'time');
  }

  /**
   * Normalizes a survey time value to a 24h `HH:mm:ss` string (e.g. "14:30" → "14:30:00"), matching
   * the `00:00:00` format agreed for the `time` type.
   */
  private formatTimeValue(value: unknown): string {
    const [hours = '0', minutes = '0', seconds = '0'] = String(value).split(':');
    const pad = (part: string): string => part.padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  private addOrUpdateMetadata(metadata: AntragMetaData, newItems: FormResult[]): AntragMetaData {
    return new AntragMetaData({
      ...metadata,
      formResults: [...newItems],
    });
  }

  private createFormResults(
    formValueMap: FormValueResultMap[],
    formId: string,
    isValid: boolean,
    isMandatory: boolean,
  ): FormResult[] {
    const stammdatenResults = this.prepareStammdatenForSend().filter((stammdatenResult) => !!stammdatenResult.name);

    const groupedResults = formValueMap.reduce(
      (formValueResultMap, { key, value }) => {
        (formValueResultMap[key] ??= []).push(value);
        return formValueResultMap;
      },
      {} as Record<string, FormValue[]>,
    );

    if (Object.keys(groupedResults).length === 0) {
      return [];
    }

    return Object.entries(groupedResults).map(([key, values]) => {
      return new FormResult({
        templateName: key,
        results: [...values, ...stammdatenResults],
        formId: Guid.create(formId),
        isValid: isValid,
        isMandatory: isMandatory ?? false,
        createdAt: new Date(),
      });
    });
  }

  private convertStammDatenKey(key: string): string {
    const keyMapping: { [key: string]: string } = {
      personalnummer: '$$_Personalnummer',
      nachname: '$$_AntragsstellerNachname',
      vorname: '$$_AntragsstellerVorname',
      gebdat: '$$_Geburtsdatum',
      email: '$$_EMail',
    };

    return keyMapping[key] ?? '';
  }

  private prepareStammdatenForSend(): FormValue[] {
    const stammdaten = this.stammdatenService.stammdaten();
    const stammdatenResult: FormValue[] = [];
    Object.keys(stammdaten).forEach((key) => {
      stammdatenResult.push(FormValue.create('text', this.convertStammDatenKey(key), stammdaten[key]));
    });
    return stammdatenResult;
  }
}
type FormValueResultMap = {
  key: string;
  value: FormValue;
};

/**
 * The subset of a SurveyJS `Question` that {@link MetadataHelpersService} needs to map a value. A real
 * `Question` satisfies it, and a synthesized `multipletext` item can be mapped to the same shape — so
 * both flow through `buildFormValues` without an `unknown` cast.
 */
type MappableQuestion = {
  name: string;
  value: unknown;
  inputType?: string;
  getType(): string;
};
